import moment from 'moment';
import Swal from 'sweetalert2';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';
import { printDispatchDocument } from './printDispatchDocuments';

const SOURCE_OPTIONS = [
    { value: 1, label: 'يدوي', icon: 'ti-edit' },
    { value: 2, label: 'من طلب معتمد', icon: 'ti-clipboard-check' },
    { value: 3, label: 'مباشر (طوارئ)', icon: 'ti-emergency-bed' },
];

const STATUS_OPTIONS = [
    { value: 1, label: 'مسودة', tone: 'secondary' },
    { value: 2, label: 'مطبوع — في انتظار الاستلام', tone: 'info' },
    { value: 3, label: 'مستلم — في انتظار سند الدخول', tone: 'warning' },
    { value: 4, label: 'مكتمل', tone: 'success' },
    { value: 9, label: 'ملغي', tone: 'danger' },
];

export default {
    name: 'DispatchV2List',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية للوصول إلى صرف V2');
            this.href('/Home');
            return;
        }
        await this.loadBranches();
        await this.GetStatistics();
        this.GetInfo(1);
    },

    computed: {
        canViewPage() {
            if (!this.loginDetails) return false;
            if (this.hasInventoryAndStockAccessUi() || this.canDispenseFromMinistryUi()) return true;
            if (this.isMinistryPharmacyDepartmentRole()) return true;
            return false;
        },
        canManage() {
            return this.hasInventoryAndStockAccessUi() || this.canDispenseFromMinistryUi();
        },
        canConfirmReceipt() {
            return this.isFacilityCenterRole() && !!this.loginDetails && !!this.loginDetails.branchesId;
        },
        statsCards() {
            const s = this.Statistics || {};
            return [
                this.statCard('عمليات صرف قيد التنفيذ', s.inProgress, 'warning', 'ti ti-clock'),
                this.statCard('عمليات صرف مكتملة', s.completed, 'success', 'ti ti-circle-check'),
                this.statCard('عمليات صرف ملغية', s.cancelled, 'danger', 'ti ti-circle-x'),
                this.statCard('إجمالي الكمية المصروفة (الشهر)', s.monthlyQuantity, 'info', 'ti ti-package'),
            ];
        },
        sourceOptions() { return SOURCE_OPTIONS; },
        statusOptions() { return STATUS_OPTIONS; },
        facilityBranches() {
            return (this.branchesList || []).filter(b => b.status !== 9);
        },
        statusBadgeClass() {
            return (status) => {
                const opt = STATUS_OPTIONS.find(x => x.value === Number(status));
                return opt ? `badge bg-label-${opt.tone}` : 'badge bg-label-secondary';
            };
        },
        statusLabelOf() {
            return (status) => {
                const opt = STATUS_OPTIONS.find(x => x.value === Number(status));
                return opt ? opt.label : '—';
            };
        },
        sourceChipClass() {
            return (t) => {
                if (Number(t) === 2) return 'source-chip source-fromorder';
                if (Number(t) === 3) return 'source-chip source-direct';
                return 'source-chip';
            };
        },
        sourceLabelOf() {
            return (t) => {
                const opt = SOURCE_OPTIONS.find(x => x.value === Number(t));
                return opt ? opt.label : '—';
            };
        },
    },

    data() {
        return {
            ScreenTitle: 'الصرف (V2)',
            pageNo: 1,
            pageSize: 10,
            pages: 0,
            FilterFacility: '',
            FilterStatus: '',
            FilterFrom: '',
            FilterTo: '',
            Search: '',
            Info: [],
            Statistics: {},
            branchesList: [],
        };
    },

    filters: {
        moment(d) { return d ? moment(d).format('DD/MM/YYYY') : '—'; },
    },

    methods: {
        tableRowNumber(i) {
            return ((Number(this.pageNo) - 1) * Number(this.pageSize)) + i + 1;
        },

        extractError(err) {
            if (err && err.response && err.response.data) {
                return typeof err.response.data === 'string' ? err.response.data : (err.response.data.message || 'حدث خطأ غير متوقع');
            }
            return (err && err.message) || 'حدث خطأ غير متوقع';
        },

        async loadBranches() {
            try {
                const res = await this.$http.GetAllBranches();
                this.branchesList = (res && res.data && res.data.info) || [];
            } catch { this.branchesList = []; }
        },

        ApplyFilters() { this.GetInfo(1); },

        ResetFilters() {
            this.FilterFacility = '';
            this.FilterStatus = '';
            this.FilterFrom = '';
            this.FilterTo = '';
            this.Search = '';
            this.GetInfo(1);
        },

        GetStatistics() {
            return this.$http.GetDispatchStatistics()
                .then(res => { this.Statistics = (res && res.data) || {}; })
                .catch(() => { this.Statistics = {}; });
        },

        GetInfo(pageNo) {
            this.pageNo = pageNo || this.pageNo || 1;
            if (!this.Search) this.$blockUI.Start();
            this.$http.GetDispatch(
                this.pageNo,
                this.pageSize,
                this.Search,
                this.FilterFacility || null,
                this.FilterStatus === '' ? null : Number(this.FilterStatus),
                this.FilterFrom || null,
                this.FilterTo || null,
            ).then(res => {
                if (!this.Search) this.$blockUI.Stop();
                this.Info = (res.data && res.data.info) || [];
                this.pages = (res.data && res.data.count) || 0;
            }).catch(() => {
                if (!this.Search) this.$blockUI.Stop();
                this.Info = [];
                this.pages = 0;
            });
        },

        GoCreate() {
            this.$router.push({ name: 'DispatchV2Create' });
        },

        GoEdit(item) {
            this.$router.push({ name: 'DispatchV2Edit', params: { id: item.id } });
        },

        PrintIssueVoucher(item) {
            Swal.fire({
                title: 'تأكيد طباعة سند الصرف',
                text: 'سيتم خصم الكميات من المخزن المصدر.',
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-primary me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد وطباعة',
                cancelButtonText: 'إلغاء',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchPrintIssueVoucher(item.id)
                    .then(async () => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم خصم الكميات');
                        this.GetInfo(this.pageNo);
                        this.GetStatistics();
                        const res = await this.$http.GetDispatchOne(item.id);
                        if (res && res.data) printDispatchDocument(res.data, 'issue', { logoUrl: this.logoPath });
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },

        PrintFinalInvoice(item) {
            Swal.fire({
                title: 'تأكيد طباعة الفاتورة النهائية',
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-primary me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد وطباعة',
                cancelButtonText: 'إلغاء',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchPrintFinalInvoice(item.id)
                    .then(async () => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم تحديث الحالة');
                        this.GetInfo(this.pageNo);
                        this.GetStatistics();
                        const res = await this.$http.GetDispatchOne(item.id);
                        if (res && res.data) printDispatchDocument(res.data, 'finalInvoice', { logoUrl: this.logoPath });
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },

        CancelDispatch(item) {
            Swal.fire({
                title: 'إلغاء عملية الصرف',
                text: 'الرجاء إدخال سبب الإلغاء',
                input: 'textarea',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-danger me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'إلغاء العملية',
                cancelButtonText: 'تراجع',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchCancel(item.id, result.value || '')
                    .then(() => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم إلغاء العملية');
                        this.GetInfo(this.pageNo);
                        this.GetStatistics();
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },
    },
};
